<template>
    <div class="container-fluid">
      
      <!-- Header -->
      <div class="row my-3">
        <div class="col-12">
          <h2 class="mb-0">Dashboard</h2>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-4">
        
        <!-- Open Jobs Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Open Jobs</h6>
                  <h2 class="card-title mb-0">{{ openJobsCount }}</h2>
                </div>
                <div class="rounded-circle bg-primary bg-opacity-10 p-3">
                  <i class="fas fa-briefcase fa-lg text-primary"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i> 
                <a href="#/job/list" class="text-decoration-none">View all jobs</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Jobs Closed This Week Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Closed This Week</h6>
                  <h2 class="card-title mb-0">{{ closedThisWeekCount }}</h2>
                </div>
                <div class="rounded-circle bg-success bg-opacity-10 p-3">
                  <i class="fas fa-check-circle fa-lg text-success"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">Since {{ weekStartDate }}</p>
            </div>
          </div>
        </div>

        <!-- Old Jobs Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Old Jobs (30+ Days)</h6>
                  <h2 class="card-title mb-0">{{ oldJobsCount }}</h2>
                </div>
                <div class="rounded-circle bg-warning bg-opacity-10 p-3">
                  <i class="fas fa-exclamation-triangle fa-lg text-warning"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">Require attention</p>
            </div>
          </div>
        </div>

        <!-- Hours This Week Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Hours This Week</h6>
                  <h2 class="card-title mb-0">{{ hoursThisWeek }}</h2>
                </div>
                <div class="rounded-circle bg-info bg-opacity-10 p-3">
                  <i class="fas fa-clock fa-lg text-info"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/time/list" class="text-decoration-none">View time entries</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Running Clocks Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Running Clocks</h6>
                  <h2 class="card-title mb-0">{{ runningClocksCount }}</h2>
                </div>
                <div class="rounded-circle bg-success bg-opacity-10 p-3">
                  <i class="fas fa-play-circle fa-lg text-success"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/clock/list" class="text-decoration-none">View clocks</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Pending Parts Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Pending Parts</h6>
                  <h2 class="card-title mb-0">{{ pendingPartsCount }}</h2>
                </div>
                <div class="rounded-circle bg-secondary bg-opacity-10 p-3">
                  <i class="fas fa-cog fa-lg text-secondary"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/part/list" class="text-decoration-none">View parts</a>
              </p>
            </div>
          </div>
        </div>

      </div>

      <!-- Bottom Full-Width Section -->
      <div class="row">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <h5 class="card-title mb-3">Activity Timeline</h5>
              <!-- Activity Graph -->
              <activity-graph></activity-graph>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import ActivityGraph from "@/components/Graphs/ActivityGraph.vue";
import ApiService from "@/services/ApiService";
import { useAppStore } from "@/stores/AppStore";
import { RequestFilter } from "@/models/RequestModel";
import { TimeModelFilter } from "@/models/TaskModel";
import { PartModelFilter } from "@/models/PartModel";

const appStore = useAppStore();

// User info
const userName = computed(() => {
  return appStore.account.user?.Email?.split('@')[0] || 'User';
});

const currentUserId = computed(() => {
  return appStore.account.user?.UserId || 0;
});

// Stats
const openJobsCount = ref(0);
const closedThisWeekCount = ref(0);
const oldJobsCount = ref(0);
const hoursThisWeek = ref('0.0');
const runningClocksCount = ref(0);
const pendingPartsCount = ref(0);

// Helper to get week start date
const weekStartDate = computed(() => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as week start
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  return monday.toLocaleDateString();
});

// Calculate date ranges
const getWeekStart = (): Date => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const get30DaysAgo = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
};

// Load open jobs
const loadOpenJobs = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [1, 2, 3, 4, 5] as Array<Number>; // Active statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    openJobsCount.value = response.Meta.Total || 0;
  } catch (error) {
    console.error('Error loading open jobs:', error);
  }
};

// Load jobs closed this week
const loadClosedThisWeek = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [6, 7] as Array<Number>; // Closed/Completed statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    
    // Filter by completion date this week
    const weekStart = getWeekStart();
    const closedThisWeek = (response.Data || []).filter(job => {
      if (!job.CompletionDate) return false;
      const completionDate = new Date(job.CompletionDate);
      return completionDate >= weekStart;
    });
    
    closedThisWeekCount.value = closedThisWeek.length;
  } catch (error) {
    console.error('Error loading closed jobs:', error);
  }
};

// Load old jobs
const loadOldJobs = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [1, 2, 3, 4, 5] as Array<Number>; // Active statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    
    // Filter by create date over 30 days ago
    const thirtyDaysAgo = get30DaysAgo();
    const oldJobs = (response.Data || []).filter(job => {
      if (!job.CreateDate) return false;
      const createDate = new Date(job.CreateDate);
      return createDate < thirtyDaysAgo;
    });
    
    oldJobsCount.value = oldJobs.length;
  } catch (error) {
    console.error('Error loading old jobs:', error);
  }
};

// Load hours this week
const loadHoursThisWeek = async () => {
  try {
    const filter = new TimeModelFilter();
    const weekStart = getWeekStart();
    filter.StartDate = weekStart;
    filter.EndDate = new Date();
    filter.TechId = [currentUserId.value as number];
    filter.Size = 9999;
    
    const response = await ApiService.loadTimes(filter, true);
    
    // Sum up the hours
    const totalMinutes = (response.Data || []).reduce((sum, entry) => {
      return sum + (entry.ActualTime || 0);
    }, 0);
    
    const hours = (totalMinutes / 60).toFixed(1);
    hoursThisWeek.value = hours;
  } catch (error) {
    console.error('Error loading hours:', error);
  }
};

// Load running clocks
const loadRunningClocks = async () => {
  try {
    const clocks = await ApiService.loadClocks(true);
    runningClocksCount.value = clocks.length;
  } catch (error) {
    console.error('Error loading clocks:', error);
  }
};

// Load pending parts
const loadPendingParts = async () => {
  try {
    const filter = new PartModelFilter();
    filter.TechId = [currentUserId.value as number];
    filter.OrderStatus = [1, 2] as Array<number>; // Pending/Ordered statuses
    filter.Size = 9999;
    
    const response = await ApiService.loadParts(filter, true);
    pendingPartsCount.value = response.Meta.Total || 0;
  } catch (error) {
    console.error('Error loading parts:', error);
  }
};

// Load all dashboard data
const loadDashboard = async () => {
  await Promise.all([
    loadOpenJobs(),
    loadClosedThisWeek(),
    loadOldJobs(),
    loadHoursThisWeek(),
    loadRunningClocks(),
    loadPendingParts()
  ]);
};

onMounted(() => {
  loadDashboard();
});

</script>